// src/modules/app-views/crm/pages/Boards/tabs/VoipTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Col, Grid, Row } from 'antd';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import { getStorageAuthItem } from 'stores/auth';
import DialPad from './DialPad';
import ExtensionsList from './ExtensionsList';
import CallHistory from './CallHistory';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const ACTIVE_STATUSES = ['initiated', 'ringing', 'answered'];
const POLL_INTERVAL_ACTIVE = 5_000; // 5s while there are active calls
const POLL_INTERVAL_POST_CALL = 15_000; // 15s after call ends (waiting for audio/transcription)
const POST_CALL_DURATION = 120_000; // keep polling 2min after last call ends

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const VoipTab = props => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { onReady, active } = props;

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const initialLoadDone = useRef(false);
  const eventSourceRef = useRef(null);
  const pollTimerRef = useRef(null);
  const lastActiveCallTimeRef = useRef(0);
  const sseConnectedRef = useRef(false);
  const hasActiveCallsRef = useRef(false);
  const screens = Grid.useBreakpoint();
  const storedSubscription = useZustand('auth', 'subscription');
  const subscriptionId = storedSubscription?._id;

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [connected, setConnected] = useState(false);
  const [lines, setLines] = useState([]);
  const [userLineMapping, setUserLineMapping] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [calling, setCalling] = useState(false);

  // Call history state (lifted to preserve across breakpoint changes)
  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsPage, setCallsPage] = useState(1);
  const [callsTotalPages, setCallsTotalPages] = useState(1);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    (async () => {
      try {
        const [data] = await axiosService.get({
          url: API.GOTO.CONNECTION_STATUS,
          returnAsObject: true,
        });

        if (data?.connected) {
          setConnected(true);
          setLines(data.data?.lines || []);
          setUserLineMapping(data.userLineMapping || null);
        } else {
          setConnected(false);
        }
      } catch {
        setConnected(false);
      } finally {
        onReady?.();
      }
    })();
  }, [onReady]);

  // Fetch call history when connected
  useEffect(() => {
    if (connected) {
      fetchCallHistory(1);
    }
  }, [connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE: Subscribe to real-time call events (with polling fallback)
  useEffect(() => {
    if (!connected || !subscriptionId) return;

    let es;
    let reconnectTimer;

    (async () => {
      try {
        const { default: apiConfig } = await import('configs/fetchInterceptor');
        const baseUrl = apiConfig.defaults.baseURL || '';
        const token = getStorageAuthItem('token');
        const sseUrl = `${baseUrl}${API.GOTO.CALLS_EVENTS}/${subscriptionId}?token=${encodeURIComponent(token)}`;

        es = new EventSource(sseUrl);
        eventSourceRef.current = es;

        es.onopen = () => {
          sseConnectedRef.current = true;
        };

        // On any goto event, silently re-fetch call history (page 1)
        const handleGotoEvent = () => {
          fetchCallHistory(1, true);
        };

        es.addEventListener('goto:call-started', handleGotoEvent);
        es.addEventListener('goto:call-answered', handleGotoEvent);
        es.addEventListener('goto:call-ended', handleGotoEvent);
        es.addEventListener('goto:post-call-completed', handleGotoEvent);

        // Also listen for generic message events (backend may use different format)
        es.onmessage = () => {
          fetchCallHistory(1, true);
        };

        es.onerror = () => {
          sseConnectedRef.current = false;
          es.close();
          eventSourceRef.current = null;

          // Try reconnecting after 10s
          reconnectTimer = setTimeout(() => {
            fetchCallHistory(1, true);
          }, 10_000);
        };
      } catch {
        sseConnectedRef.current = false;
      }
    })();

    return () => {
      clearTimeout(reconnectTimer);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (es) {
        es.close();
      }
      sseConnectedRef.current = false;
    };
  }, [connected, subscriptionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start/stop polling when connection status changes or after a call is initiated
  useEffect(() => {
    if (!connected) return;

    // Start polling immediately — the interval callback reads hasActiveCallsRef
    startPolling();

    return () => stopPolling();
  }, [connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleDigit = useCallback(digit => {
    setPhoneNumber(prev => prev + digit);
  }, []);

  const handleBackspace = useCallback(() => {
    setPhoneNumber(prev => prev.slice(0, -1));
  }, []);

  const handleClear = useCallback(() => {
    setPhoneNumber('');
  }, []);

  const handleCall = useCallback(async to => {
    const target = to || phoneNumber;
    if (!target) return;

    setCalling(true);
    try {
      const e164 = buildE164(target);
      await axiosService.post({
        url: API.GOTO.CALLS,
        data: { to: e164 },
      });
      appService.message({ flag: 's', text: 'goto_call_initiated' });

      // Immediately fetch to show the new call in history and start polling
      lastActiveCallTimeRef.current = Date.now();
      hasActiveCallsRef.current = true;
      startPolling();
      setTimeout(() => fetchCallHistory(1, true), 1_000);
    } catch {
      appService.message({ flag: 'e', text: 'goto_call_error' });
    } finally {
      setCalling(false);
    }
  }, [phoneNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCallExtension = useCallback(line => {
    handleCall(line.number);
  }, [handleCall]);

  const handleCallsPageChange = useCallback(pageNum => {
    fetchCallHistory(pageNum);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  // Responsive breakpoint logic:
  // md = tablet (768-991px): 2 cols, CallHistory below DialPad in same col
  // lg = desktop (>=992px): 3 cols, CallHistory in own col
  // xs = mobile (<768px): stacked, CallHistory in own col
  const isMd = screens.md && !screens.lg;
  const isLgOrXs = screens.lg || !screens.md;

  const callHistoryBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', height: screens.lg ? '100%' : undefined, overflowY: screens.lg ? 'auto' : undefined }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 8,
          color: 'var(--ant-color-text-secondary)',
        }}
      >
        {translateX('goto_call_history')}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <CallHistory
          calls={calls}
          loading={callsLoading}
          page={callsPage}
          totalPages={callsTotalPages}
          onPageChange={handleCallsPageChange}
          onRedial={handleCall}
          calling={calling}
        />
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  // GoTo not connected
  if (!connected) {
    return (
      <Alert
        type="warning"
        showIcon
        title={translateX('goto_not_connected')}
        description={translateX('goto_not_connected_dialer')}
      />
    );
  }

  return (
    <div style={{ height: screens.lg ? '100%' : undefined, overflow: screens.lg ? 'hidden' : undefined }}>
      <Row gutter={[16, 16]} style={{ height: screens.lg ? '100%' : undefined }}>
        {/* Dial pad + (on tablet: call history below) */}
        <Col xs={24} md={10} lg={8} style={{ height: screens.lg ? '100%' : undefined }}>
          <div style={{ height: screens.lg ? '100%' : undefined, overflowY: screens.lg ? 'auto' : undefined }}>
            <DialPad
              phoneNumber={phoneNumber}
              onDigit={handleDigit}
              onBackspace={handleBackspace}
              onCall={() => handleCall()}
              onClear={handleClear}
              calling={calling}
              active={active}
            />
            {/* On tablet (md), render call history below dial pad */}
            {isMd && <div style={{ marginTop: 16 }}>{callHistoryBlock}</div>}
          </div>
        </Col>

        {/* Extensions list */}
        <Col xs={24} md={14} lg={8} style={{ height: screens.lg ? '100%' : undefined }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: screens.lg ? '100%' : undefined, overflowY: screens.lg ? 'auto' : undefined }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 8,
                color: 'var(--ant-color-text-secondary)',
              }}
            >
              {translateX('goto_extensions')}
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ExtensionsList
                lines={lines}
                userLineMapping={userLineMapping}
                onCallExtension={handleCallExtension}
                calling={calling}
              />
            </div>
          </div>
        </Col>

        {/* Call history — own column on desktop (lg) and mobile (xs) */}
        {isLgOrXs && (
          <Col xs={24} lg={8} style={{ height: screens.lg ? '100%' : undefined }}>
            {callHistoryBlock}
          </Col>
        )}
      </Row>
    </div>
  );

  // ---------------------------------------------------------------------------
  // INTERNAL FUNCTIONS
  // ---------------------------------------------------------------------------

  // Fetch call history; silent=true skips loading spinner (used by polling/SSE)
  async function fetchCallHistory(pageNum, silent = false) {
    if (!silent) setCallsLoading(true);

    try {
      // Encode pagination query in Jarvisly standard format (base64 in ?q= param)
      const paginationQuery = {
        pagination: { page: pageNum, size: 20 },
        sort: [{ field: 'initiatedAt', order: 'desc' }],
        search: {},
        textSearch: '',
        filter: {},
        drawer: { folder: 'inbox' },
      };
      const q = encodeURIComponent(btoa(JSON.stringify(paginationQuery)));
      const url = `${API.GOTO.CALLS_PAGINATION}?q=${q}`;

      const [data, result] = await axiosService.get({ url });
      const pagination = result?.summary?.pagination;

      const newCalls = data || [];
      setCalls(newCalls);
      setCallsPage(pagination?.page || 1);
      setCallsTotalPages(pagination?.pages || 1);

      // Update active calls ref (drives polling logic without re-rendering)
      const hadActive = hasActiveCallsRef.current;
      hasActiveCallsRef.current = newCalls.some(c => ACTIVE_STATUSES.includes(c.status));

      if (hasActiveCallsRef.current) {
        lastActiveCallTimeRef.current = Date.now();
      }

      // Start or restart polling on active status change
      if (hadActive !== hasActiveCallsRef.current) {
        startPolling();
      }
    } catch {
      if (!silent) setCalls([]);
    } finally {
      if (!silent) setCallsLoading(false);
    }
  }

  // Start or restart the polling timer based on current state
  function startPolling() {
    stopPolling();

    const hasActive = hasActiveCallsRef.current;
    const inPostCallWindow = Date.now() - lastActiveCallTimeRef.current < POST_CALL_DURATION;

    if (!hasActive && !inPostCallWindow) return;

    const interval = hasActive ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_POST_CALL;

    pollTimerRef.current = setInterval(async () => {
      // Check if post-call window expired
      if (!hasActiveCallsRef.current && Date.now() - lastActiveCallTimeRef.current >= POST_CALL_DURATION) {
        stopPolling();
        return;
      }

      // Sync stale calls with GoTo API before fetching history
      if (hasActiveCallsRef.current) {
        try {
          await axiosService.post({ url: API.GOTO.CALLS_SYNC });
        } catch (err) {
          console.warn('[VoipTab] sync failed:', err?.response?.status || err?.message);
        }
      }

      fetchCallHistory(1, true);
    }, interval);
  }

  function stopPolling() {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default VoipTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Normalize phone number to E.164 format for Brazilian numbers.
 * - Short extensions (<=6 digits) stay as-is
 * - 10-11 digit BR numbers get +55 prefix
 * - Already E.164 (starts with +) stays as-is
 */
function buildE164(num) {
  if (!num) return '';
  const digits = num.replace(/\D/g, '');

  // Short extension — pass through
  if (digits.length <= 6) return digits;

  // Already has country code
  if (num.startsWith('+')) return num;

  // BR number: 10 digits (landline) or 11 digits (mobile)
  if (digits.length === 10 || digits.length === 11) {
    return `+55${digits}`;
  }

  // Fallback: return with + prefix if long enough
  return `+${digits}`;
}
